import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useHistory } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import {
  AutoInvoiceFormProvider,
  useAutoInvoiceFormContext,
} from "./context/AutoInvoiceFormContext";
import AutoInvoiceFormHeader from "./sections/AutoInvoiceFormHeader";
import AutoInvoiceFormProviderSection from "./sections/AutoInvoiceFormProviderSection";
import AutoInvoiceFormTransactionLocationSection from "./sections/AutoInvoiceFormTransactionLocationSection";
import AutoInvoiceFormCertificateSection from "./sections/AutoInvoiceFormCertificateSection";
import AutoInvoiceFormCurrencySection from "./sections/AutoInvoiceFormCurrencySection";
import AutoInvoiceFormItems from "./sections/AutoInvoiceFormItems";
import Loading from "../indicators/Loading";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import { errorAlert, successAlert } from "../utils/messages";
import { getRoutePath } from "../../routes";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import {
  getCityByCode,
  getDepartmentByCode,
  getDistrictByCode,
} from "../../api/locations";
import {
  AutoInvoice,
  AUTO_INVOICE_STATUS_UPLOADED_TO_SET,
  AUTO_INVOICE_STATUS_APPROVED_BY_SET,
  AUTO_INVOICE_PROVIDER_TYPE_NO_RUC,
  AUTO_INVOICE_PROVIDER_DOCUMENT_TYPE_PARAGUAYAN_DNI,
} from "../../models/auto-invoice";
import { PYG } from "../../currency/available-currencies";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";

const AutoInvoiceFormInnerContent: React.FC = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );

  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();

  const {
    autoInvoiceRef,
    setErrors,
    setAutoInvoiceDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setProviderDepartment,
    setProviderDistrict,
    setProviderCity,
    setTransactionDepartment,
    setTransactionDistrict,
    setTransactionCity,
    setCurrency,
    setPygExchangeRate,
    setApplyDiscount,
    onItemsChange,
  } = useAutoInvoiceFormContext();

  const name = "Agregar Autofactura Electrónica";

  const getEditingAutoInvoiceId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingInvoiceId = urlParams.get("id");

    if (editingInvoiceId !== null) {
      if (!isNaN(parseInt(editingInvoiceId))) {
        return parseInt(editingInvoiceId);
      }
    }

    return undefined;
  };

  const loadInitialInvoiceById = async (): Promise<AutoInvoice | null> => {
    const editingAutoInvoiceId = getEditingAutoInvoiceId();

    if (editingAutoInvoiceId === undefined) {
      return null;
    }

    let requestStatus = await getItem<AutoInvoice>(
      `/auto_invoices/${editingAutoInvoiceId}/`
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const autoInvoice = await loadInitialInvoiceById();

    if (autoInvoice === null) {
      setLoading(false);
      return;
    }

    if (
      autoInvoice.status === AUTO_INVOICE_STATUS_UPLOADED_TO_SET ||
      autoInvoice.status === AUTO_INVOICE_STATUS_APPROVED_BY_SET
    ) {
      setLoading(false);
      setLoadError(true);
    }

    const stampPromise = getStampById(autoInvoice.stampId);
    const establishmentPromise = getEstablishmentById(
      autoInvoice.establishmentId
    );
    const dispatchPointPromise = getDispatchPointById(
      autoInvoice.dispatchPointId
    );

    const providerDepartmentPromise = getDepartmentByCode(
      autoInvoice.providerDepartment
    );
    const providerDistrictPromise = getDistrictByCode(
      autoInvoice.providerDistrict
    );
    const providerCityPromise = getCityByCode(autoInvoice.providerCity);

    const transactionDepartmentPromise = getDepartmentByCode(
      autoInvoice.transactionDepartment
    );
    const transactionDistrictPromise = getDistrictByCode(
      autoInvoice.transactionDistrict
    );
    const transactionCityPromise = getCityByCode(autoInvoice.transactionCity);

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const providerDepartment = await providerDepartmentPromise;
    const providerDistrict = await providerDistrictPromise;
    const providerCity = await providerCityPromise;
    const transactionDepartment = await transactionDepartmentPromise;
    const transactionDistrict = await transactionDistrictPromise;
    const transactionCity = await transactionCityPromise;

    setProviderDepartment(providerDepartment);
    setProviderDistrict(providerDistrict);
    setProviderCity(providerCity);
    setTransactionDepartment(transactionDepartment);
    setTransactionDistrict(transactionDistrict);
    setTransactionCity(transactionCity);

    autoInvoiceRef.current = autoInvoice;

    const pygDate = new Date(autoInvoice.autoInvoiceDate!);
    const year = pygDate.getUTCFullYear();
    const month = pygDate.getUTCMonth();
    const day = pygDate.getUTCDate();
    const hours = pygDate.getUTCHours();
    const minutes = pygDate.getUTCMinutes();
    const seconds = pygDate.getUTCSeconds();
    const autoInvoiceDate = new Date(year, month, day, hours, minutes, seconds);
    setAutoInvoiceDate(autoInvoiceDate);

    if (autoInvoice.globalDiscount !== undefined) {
      setApplyDiscount(true);
    }
    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    onItemsChange(autoInvoice.items);

    setCurrency(autoInvoice.currency ? autoInvoice.currency : PYG);
    setPygExchangeRate(autoInvoice.pygExchangeRate);

    setLoading(false);
  };

  useEffect(() => {
    const newAutoInvoiceDate = changeTZToPyg(new Date());
    if (autoInvoiceRef.current !== null) {
      autoInvoiceRef.current.autoInvoiceDate =
        changeTZToUtc(newAutoInvoiceDate).toISOString();
      setAutoInvoiceDate(newAutoInvoiceDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select stamp when only one exists
  useEffect(() => {
    if (
      stamps.length === 1 &&
      autoInvoiceRef.current?.id === undefined &&
      autoInvoiceRef.current !== null
    ) {
      const stamp = stamps[0];
      autoInvoiceRef.current.stampId = stamp.id;
      autoInvoiceRef.current.stampIdentifier = stamp.identifier;
      autoInvoiceRef.current.stampBeginDate = stamp.beginDate;
      setStamp(stamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps]);

  // Auto-select establishment when only one exists
  useEffect(() => {
    if (
      establishments.length === 1 &&
      autoInvoiceRef.current?.id === undefined &&
      autoInvoiceRef.current !== null
    ) {
      const establishment = establishments[0];
      autoInvoiceRef.current.establishmentId = establishment.id;
      autoInvoiceRef.current.establishmentCode = establishment.code;
      setEstablishment(establishment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments]);

  // Auto-select dispatch point when only one exists
  useEffect(() => {
    if (
      dispatchPoints.length === 1 &&
      autoInvoiceRef.current?.id === undefined &&
      autoInvoiceRef.current !== null
    ) {
      const dispatchPoint = dispatchPoints[0];
      autoInvoiceRef.current.dispatchPointId = dispatchPoint.id;
      autoInvoiceRef.current.dispatchPointIdentifier = dispatchPoint.identifier;
      setDispatchPoint(dispatchPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchPoints]);

  const onSave = async () => {
    if (autoInvoiceRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<AutoInvoice>>;
    let successMessage = "Factura agregada exitósamente";

    const autoInvoice = autoInvoiceRef.current;
    autoInvoice.taxPayerRuc = taxPayer.ruc;
    autoInvoice.taxPayerSocialName = taxPayer.socialName;
    autoInvoice.taxPayerFantasyName = taxPayer.fantasyName;

    if (autoInvoice.currency === undefined) {
      autoInvoice.currency = PYG;
    }

    if (autoInvoice.providerType === undefined) {
      autoInvoice.providerType = AUTO_INVOICE_PROVIDER_TYPE_NO_RUC;
    }

    if (autoInvoice.providerDocumentType === undefined) {
      autoInvoice.providerDocumentType =
        AUTO_INVOICE_PROVIDER_DOCUMENT_TYPE_PARAGUAYAN_DNI;
    }

    if (autoInvoice.id === undefined) {
      requestPromise = createItem<AutoInvoice>(
        "/auto_invoices/create/",
        autoInvoice,
        { "Idempotency-Key": idempotencyKey },
      );
    } else {
      requestPromise = updateItem<AutoInvoice>(
        `/auto_invoices/${autoInvoice.id}/`,
        autoInvoice
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      history.push(getRoutePath("autoInvoices"));
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            fontWeight: 600,
            fontSize: "1rem",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="fa fa-file-text-o" style={{ fontSize: "1rem" }}></i>
          {name}
        </CCardHeader>
        <CCardBody>
          <AutoInvoiceFormHeader />
          <AutoInvoiceFormProviderSection />
          <AutoInvoiceFormTransactionLocationSection />
          <AutoInvoiceFormCertificateSection />
          <AutoInvoiceFormCurrencySection />
          <AutoInvoiceFormItems />
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          <CButton
            type="submit"
            color="primary"
            onClick={onSave}
            disabled={submitting}
            style={{ minWidth: 130, fontWeight: 600 }}
          >
            {submitting ? (
              <Spinner
                animation="grow"
                style={{
                  height: "17px",
                  width: "17px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  marginRight: "10px",
                }}
              />
            ) : (
              <i className="fa fa-save mr-2"></i>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CCardFooter>
      </CCard>
    </>
  );
};

const AutoInvoiceFormWithContext: React.FC = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  return (
    <AutoInvoiceFormProvider taxPayerId={taxPayer.id}>
      <AutoInvoiceFormInnerContent />
    </AutoInvoiceFormProvider>
  );
};

export default AutoInvoiceFormWithContext;
